"use client";

import React, {forwardRef, useId, useRef} from "react";
import clsx from "clsx";
import {LuClipboardCopy} from "react-icons/lu";
import {CopyToClipboardIcon} from "@/components/utils/CopyToClipboard";

export const FieldLabel = ({ id, required, className, children }:{
    id: string; required: boolean; children: React.ReactNode; className?: string;
}) => (
    <label htmlFor={id} className={clsx("field-label", className)}>
        {children}{required && (<sup className="text-rose-500">*</sup>)}
    </label>
);

export const FieldHTMLLabel = ({ id, required, label, className }:{
    id: string; required: boolean; label: string; className?: string;
}) => {
    const html = required ? `${label}<sup class="text-rose-500">*</sup>` : label;
    return (
        <label
            htmlFor={id}
            className={clsx("field-label", className)}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

export const TextField = forwardRef<HTMLInputElement,
    Omit<React.ComponentPropsWithoutRef<'input'>, 'id'> & {
    label?: string; labelClassName?: string; inputClassName?: string;
    inputAppend?: React.ReactNode; error?: React.ReactNode; note?: string;
    noteClassName?: string; light?: boolean; clipboard?: boolean;
    clipboardTooltip?: string; clipboardAriaLabel?: string; clipboardDisabled?: boolean;
    onClipboardCopySuccess?: () => void; onClipboardCopyError?: () => void;
}>(({
    label,
    type='text',
    className,
    labelClassName,
    inputClassName,
    inputAppend,
    error,
    note,
    noteClassName,
    light,
    clipboard = false,
    clipboardTooltip,
    clipboardAriaLabel,
    clipboardDisabled,
    onClipboardCopySuccess,
    onClipboardCopyError,
    ...props
}, ref) => {
    const id = useId();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setInputRef = (node: HTMLInputElement | null) => {
        inputRef.current = node;

        if (typeof ref === "function") {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    return (
        <div className={className}>
            {label && <FieldLabel id={id} required={!!props.required} className={labelClassName}>{label}</FieldLabel>}
            <div className="relative">
                <input
                    ref={setInputRef}
                    id={id}
                    type={type}
                    {...props}
                    className={clsx("field", error && "field-error", light && !error && "bg-zinc-100 disabled:bg-zinc-100", clipboard && "pr-14!", inputClassName)}
                    /* Optional data-driven styling:
                       data-invalid={!!error || undefined}
                       className={clsx("field data-invalid:field-error", inputClassName)}
                    */
                />
                {inputAppend}
                {clipboard && (
                    <CopyToClipboardIcon
                        content={<LuClipboardCopy aria-hidden="true" className="h-5 w-5"/>}
                        clipboard={() => inputRef.current?.value ?? ""}
                        tooltip={clipboardTooltip}
                        ariaLabel={clipboardAriaLabel || label}
                        disabled={clipboardDisabled || props.disabled}
                        onCopySuccess={onClipboardCopySuccess}
                        onCopyError={onClipboardCopyError}
                        wrapperClassNames="absolute inset-y-0 right-0 flex w-12 items-center justify-center"
                        classNames={clsx(
                            "flex h-full w-full items-center justify-center rounded-r-md border-l border-zinc-200/80",
                            "text-zinc-500 transition-colors hover:text-zinc-900",
                            light ? "hover:bg-zinc-200" : "hover:bg-zinc-100",
                            "focus:outline-hidden focus:ring-4 ring-color",
                            "disabled:cursor-not-allowed disabled:opacity-40",
                        )}
                    />
                )}
            </div>
            {note && (<div className={clsx("field-note", noteClassName)}>{note}</div>)}
            {error && (<div className="field-error-text">{error}</div>)}
        </div>
    );
});

TextField.displayName = 'TextField';

export const TextAreaField = forwardRef<HTMLTextAreaElement,
    Omit<React.ComponentPropsWithoutRef<'textarea'>, 'id'> & {
    label?: string; labelClassName?: string; textareaClassName?: string;
    textareaAppend?: React.ReactNode; error?: React.ReactNode; note?: string; noteClassName?: string;
}
>(({ label, className, labelClassName, textareaClassName, textareaAppend, error, note, noteClassName, ...props }, ref) => {
    const id = useId();
    return (
        <div className={className}>
            {label && <FieldLabel id={id} required={!!props.required} className={labelClassName}>{label}</FieldLabel>}
            <div className="relative">
        <textarea
            ref={ref}
            id={id}
            {...props}
            className={clsx("field field-textarea", error && "field-error", textareaClassName)}
        />
                {textareaAppend}
            </div>
            {note && (<div className={clsx("field-note", noteClassName)}>{note}</div>)}
            {error && (<div className="field-error-text">{error}</div>)}
        </div>
    );
});

TextAreaField.displayName = 'TextAreaField';

export const SelectField = forwardRef<HTMLSelectElement,
    Omit<React.ComponentPropsWithoutRef<'select'>, 'id'> & {
    children: React.ReactNode; label?: string; labelClassName?: string;
    selectClassName?: string; selectAppend?: React.ReactNode; error?: React.ReactNode;
    note?: string; noteClassName?: string;
}
>(({ children, label, className, labelClassName, selectClassName, selectAppend, error, note, noteClassName, ...props }, ref) => {
    const id = useId();
    return (
        <div className={className}>
            {label && <FieldLabel id={id} required={!!props.required} className={labelClassName}>{label}</FieldLabel>}
            <div className="relative">
                <select
                    ref={ref}
                    id={id}
                    {...props}
                    className={clsx("field field-select", error && "field-error", selectClassName)}
                >
                    {children}
                </select>
                {selectAppend}
            </div>
            {note && (<div className={clsx("field-note", noteClassName)}>{note}</div>)}
            {error && (<div className="field-error-text">{error}</div>)}
        </div>
    );
});

SelectField.displayName = 'SelectField';

export const CheckboxField = forwardRef<HTMLInputElement,
    Omit<React.ComponentPropsWithoutRef<'input'>, 'id'> & {
    label?: string; description?: string; labelClassName?: string; inputClassName?: string; error?: React.ReactNode;
}
>(({ label, description, className, labelClassName, inputClassName, error, ...props }, ref) => {
    const id = useId();
    return (
        <div className={className}>
            <div className="flex flex-row gap-4 items-start justify-center">
                <div className="inline-flex">
                    <input
                        ref={ref}
                        type="checkbox"
                        id={id}
                        {...props}
                        className={clsx("field-checkbox", error && "field-checkbox-error", inputClassName)}
                    />
                </div>
                <div className="grow">
                    {label && <FieldHTMLLabel id={id} required={!!props.required} className={clsx(labelClassName, "grow text-left pb-0 leading-tight! cursor-pointer")} label={label} />}
                    {description && <p className="text-left text-normal md:text-lg font-source font-normal text-white leading-snug" dangerouslySetInnerHTML={{__html: description}}></p>}
                </div>
            </div>
            {error && (<div className="field-error-text">{error}</div>)}
        </div>
    );
});

CheckboxField.displayName = 'CheckboxField';
